import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import PlainTextExpandingInput from './PlainTextExpandingInput';
import { getDynamicPTS, saveDynamicPTS, getDynamicPTSProjects, createDynamicPTSProject, deleteDynamicPTSProject } from '../../api/api';
import { SUBSECTION_TEMPLATES } from '../../assets/subsectionTemplates';
import { usePTS } from '../../context/PTSContext';
import { useRakennusData } from '../../context/RakennusDataContext';
import { ReportTemplateContext } from '../report/ReportTemplate/ReportTemplateContext';
import { isProd } from '../../devprodConfig';
import { findLinkedProjectId } from '../../utils/ptsUtils';

function getActiveCategories(sections) {
  return SUBSECTION_TEMPLATES
    .filter(t => sections?.some(s =>
      s.name?.toLowerCase() === t.category.toLowerCase() ||
      s.type === t.category.toLowerCase() ||
      s.type === `section_chip_${t.category.toLowerCase()}`
    ))
    .map(t => t.ptsName ?? t.category);
}

function makeEmptyItem(numYears) {
  return { label: '', values: Array(numYears).fill('') };
}

function makeEmptySection(numYears) {
  return { kl: 'KL3', items: [makeEmptyItem(numYears)] };
}

function getKLColor(kl) {
  switch (kl) {
    case 'KL1': case 'KL2': return '#ba3b46';
    case 'KL3': return '#d0c407';
    case 'KL4': case 'KL5': return '#04aa00';
    default: return 'black';
  }
}

const isNumeric = (v) =>
  typeof v === 'number' ||
  (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)));

const YEAR_KEYS = ['y1','y2','y3','y4','y5','y6','y7','y8','y9','y10','y11'];

function apiToState(apiSections, numYears) {
  const sections = apiSections.map(s => s.name);
  const data = {};
  apiSections.forEach(s => {
    data[s.name] = {
      kl: s.kl_rating || 'KL3',
      items: s.entries.length > 0
        ? s.entries.map(e => ({
            label: e.label || '',
            values: YEAR_KEYS.map(k => e[k] ?? ''),
          }))
        : [makeEmptyItem(numYears)],
    };
  });
  return { sections, data };
}

function stateToApi(sections, data) {
  return sections.map((name, sIdx) => {
    const sec = data[name] ?? makeEmptySection(0);
    return {
      name,
      kl_rating: sec.kl || 'KL3',
      sort_order: sIdx,
      entries: (sec.items || []).map((item, iIdx) => ({
        label: item.label || '',
        ...Object.fromEntries(YEAR_KEYS.map((k, i) => [k, item.values[i] ?? ''])),
        sort_order: iIdx,
      })),
    };
  });
}

function computeStartYear() {
  const now = new Date();
  return now.getMonth() >= 7 ? now.getFullYear() + 1 : now.getFullYear();
}

export default function DynamicPTSTaulu({ data: blockData = {}, onChange }) {
  const { currentYear, tekniikkaData, lviData, sahkoData, insertTableRow } = usePTS();
  const numYears = 11;
  const { rakennusData } = useRakennusData();
  const id_kiinteisto = rakennusData?.id_kiinteisto;
  const templateCtx = useContext(ReportTemplateContext);
  const reportSections = useMemo(() => templateCtx?.sections ?? [], [templateCtx]);

  const linkedProjectId = findLinkedProjectId(reportSections);

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(blockData.projectId ?? null);
  const [loading, setLoading] = useState(true);
  const [creatingProject, setCreatingProject] = useState(false);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [data, setData] = useState({});
  const isEditing = true;
  const [newCatInput, setNewCatInput] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [showNewProjectInput, setShowNewProjectInput] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteProjectModal, setDeleteProjectModal] = useState(false);
  const [deleteProjectInput, setDeleteProjectInput] = useState('');
  const [deletingProject, setDeletingProject] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [projectName, setProjectName] = useState('Rakennetekniikka');
  const [copyModal, setCopyModal] = useState(null);
  const [copyCategory, setCopyCategory] = useState('Rakennetekniikka');
  const [copySectionIdx, setCopySectionIdx] = useState(0);
  const [projectBaseYear, setProjectBaseYear] = useState(computeStartYear);
  const years = Array.from({ length: numYears }, (_, i) => projectBaseYear + i);
  const initialLoadDone = useRef(false);

  // Load project data for a given project ID
  const loadProject = useCallback(async (projectId, reportSecs, cancelled) => {
    const ptsRes = await getDynamicPTS(projectId);
    if (cancelled?.()) return;

    const apiSections = ptsRes.data.sections ?? [];
    if (ptsRes.data.name) setProjectName(ptsRes.data.name);
    if (ptsRes.data.year) setProjectBaseYear(ptsRes.data.year);
    const { sections: s, data: d } = apiToState(apiSections, numYears);

    const active = getActiveCategories(reportSecs);
    const newCats = active.filter(cat => !s.includes(cat));
    const mergedSections = [...s, ...newCats];
    const mergedData = { ...d };
    newCats.forEach(cat => { mergedData[cat] = makeEmptySection(numYears); });

    setSections(mergedSections);
    setData(mergedData);

    if (newCats.length > 0 && !cancelled?.()) {
      try {
        await saveDynamicPTS(projectId, stateToApi(mergedSections, mergedData), ptsRes.data.name);
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2000);

      } catch (e) {
        console.warn('[DynamicPTSTaulu] auto-save failed:', e);
      }
    }
  }, [numYears]);

  // Fetch project list and load active project
  useEffect(() => {
    if (!id_kiinteisto) return;
    let isCancelled = false;
    const cancelled = () => isCancelled;

    if (!initialLoadDone.current) setLoading(true);
    setError(null);

    getDynamicPTSProjects(id_kiinteisto)
      .then(async res => {
        if (isCancelled) return;
        const list = res.data ?? [];
        setProjects(list);

        // Kuntoarviovalikko's linkedProjectId always takes priority
        let targetId = linkedProjectId ?? blockData.projectId;
        if (!targetId && list.length > 0) {
          targetId = list[0].id;
        }
        // Keep blockData.projectId in sync so the PDF renderer uses the right project
        if (targetId && targetId !== blockData.projectId) {
          onChange?.({ ...blockData, projectId: targetId });
        }

        if (targetId) {
          setActiveProjectId(targetId);
          await loadProject(targetId, reportSections, cancelled);
        }

        setLoading(false);
        initialLoadDone.current = true;
      })
      .catch(err => {
        if (!isCancelled) { setError(err.message); setLoading(false); }
      });

    return () => { isCancelled = true; };
  }, [id_kiinteisto]);

  // Re-merge active categories when report sections change (after initial load)
  // Also switches project when Kuntoarviovalikko's linkedProjectId changes
  useEffect(() => {
    if (!initialLoadDone.current) return;
    const effectiveId = linkedProjectId ?? activeProjectId;
    if (!effectiveId) return;
    if (linkedProjectId && linkedProjectId !== activeProjectId) {
      setActiveProjectId(linkedProjectId);
    }
    let isCancelled = false;
    loadProject(effectiveId, reportSections, () => isCancelled)
      .catch(err => console.warn('[DynamicPTSTaulu] reload failed:', err));
    return () => { isCancelled = true; };
  }, [reportSections, linkedProjectId]);

  const handleSelectProject = async (projectId) => {
    const id = Number(projectId);
    setActiveProjectId(id);
    onChange?.({ ...blockData, projectId: id });
    setLoading(true);
    try {
      await loadProject(id, reportSections, () => false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name || !id_kiinteisto) return;
    setCreatingProject(true);
    try {
      const res = await createDynamicPTSProject(id_kiinteisto, name, computeStartYear());
      const newProject = res.data;
      setProjects(prev => [...prev, newProject]);
      setNewProjectName('');
      setShowNewProjectInput(false);
      await handleSelectProject(newProject.id);
    } catch (err) {
      setError('Projektin luonti epäonnistui: ' + err.message);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!activeProjectId) return;
    setDeletingProject(true);
    try {
      await deleteDynamicPTSProject(activeProjectId);
      const remaining = projects.filter(p => p.id !== activeProjectId);
      setProjects(remaining);
      setDeleteProjectModal(false);
      setDeleteProjectInput('');
      const nextId = remaining[0]?.id ?? null;
      setActiveProjectId(nextId);
      onChange?.({ ...blockData, projectId: nextId });
      if (nextId) {
        await loadProject(nextId, reportSections, () => false);
      } else {
        setSections([]);
        setData({});
        setProjectName('Rakennetekniikka');
      }
    } catch (err) {
      setError('Poisto epäonnistui: ' + err.message);
    } finally {
      setDeletingProject(false);
    }
  };

  const totals = useMemo(() => {
    const sums = Array(numYears).fill(0);
    Object.values(data).forEach(sec => {
      (sec.items || []).forEach(item => {
        (item.values || []).forEach((v, i) => { sums[i] += parseFloat(v) || 0; });
      });
    });
    return sums;
  }, [data, numYears]);

  const updateSectionKL = (subcat, value) =>
    setData(prev => { const n = structuredClone(prev); n[subcat].kl = value; return n; });

  const updateItem = (subcat, itemIdx, value) =>
    setData(prev => { const n = structuredClone(prev); n[subcat].items[itemIdx].label = value; return n; });

  const updateValues = (subcat, itemIdx, yearIdx, value) =>
    setData(prev => { const n = structuredClone(prev); n[subcat].items[itemIdx].values[yearIdx] = value; return n; });

  const addRow = (subcat) =>
    setData(prev => { const n = structuredClone(prev); n[subcat].items.push(makeEmptyItem(numYears)); return n; });

  const removeRow = (subcat, itemIdx) =>
    setData(prev => {
      const n = structuredClone(prev);
      n[subcat].items.splice(itemIdx, 1);
      if (n[subcat].items.length === 0) n[subcat].items.push(makeEmptyItem(numYears));
      return n;
    });

  const removeSection = (name) => {
    setSections(prev => prev.filter(s => s !== name));
    setData(prev => { const n = structuredClone(prev); delete n[name]; return n; });
    setConfirmDelete(null);
  };

  const COPY_CATEGORIES = ['Rakennetekniikka', 'LVI Järjestelmät', 'Sähköjärjestelmät'];
  const getCopySections = (cat) =>
    ({ 'Rakennetekniikka': tekniikkaData, 'LVI Järjestelmät': lviData, 'Sähköjärjestelmät': sahkoData }[cat] ?? []);

  const isInTekniikkaPTS = (label) => {
    if (!label?.trim()) return false;
    const norm = label.trim().toLowerCase();
    return [tekniikkaData, lviData, sahkoData].some(table =>
      table?.some(sec => sec.items?.some(it => it.label?.trim().toLowerCase() === norm))
    );
  };

  const handleAddCategory = () => {
    const name = newCatInput.trim();
    if (!name || sections.includes(name)) return;
    setSections(prev => [...prev, name]);
    setData(prev => ({ ...prev, [name]: makeEmptySection(numYears) }));
    setNewCatInput('');
  };

  const savedTimerRef = useRef(null);
  const flashSaved = () => {
    setSavedFlash(true);
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedFlash(false), 2000);
  };

  const pendingSaveRef = useRef(null);
  const activeProjectIdRef = useRef(activeProjectId);
  useEffect(() => { activeProjectIdRef.current = activeProjectId; }, [activeProjectId]);

  // Autosave on data changes
  useEffect(() => {
    if (!activeProjectId) return;
    pendingSaveRef.current = { sections, data, projectName };
    const t = setTimeout(async () => {
      try {
        await saveDynamicPTS(activeProjectId, stateToApi(sections, data), projectName);
        pendingSaveRef.current = null;
        flashSaved();
      } catch (e) {
        console.warn('[DynamicPTSTaulu] autosave failed:', e);
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [data, sections, projectName]);

  // Flush pending save on unmount (section navigation cancels the debounce timeout)
  useEffect(() => {
    return () => {
      const pid = activeProjectIdRef.current;
      const pending = pendingSaveRef.current;
      if (pid && pending) {
        saveDynamicPTS(pid, stateToApi(pending.sections, pending.data), pending.projectName).catch(() => {});
      }
    };
  }, []);



  const DEV_SECTIONS = ['Julkisivut', 'Ikkunat', 'Parvekkeet'];
  const DEV_DATA = {
    'Julkisivut': { kl: 'KL2', items: [
      { label: 'Pinnoitteen uusiminen',              values: ['','150','','','','','','','','',''] },
      { label: 'Ikkunoiden tiivisteiden uusiminen',  values: ['25','','','','','','','','','',''] },
      { label: 'Sokkelipinnan korjaus',              values: ['','','45','','','','','','','',''] },
    ]},
    'Ikkunat': { kl: 'KL3', items: [
      { label: 'Ikkunoiden uusiminen',    values: ['','','','270','','','','','','',''] },
      { label: 'Ikkunoiden huoltokorjaus', values: ['40','','','','','','','','','',''] },
    ]},
    'Parvekkeet': { kl: 'KL2', items: [
      { label: 'Parvekerakenteiden korjaus', values: ['','','','','180','','','','','',''] },
      { label: 'Kaatovalujen uusiminen',     values: ['','30','','','','','','','','',''] },
    ]},
  };
  const devFillData = async () => {
    if (!activeProjectId) return;
    setSections(DEV_SECTIONS);
    setData(DEV_DATA);
    try {
      await saveDynamicPTS(activeProjectId, stateToApi(DEV_SECTIONS, DEV_DATA), projectName);
      flashSaved();
    } catch (e) {
      console.warn('[DynamicPTSTaulu] devFillData failed:', e);
    }
  };

  if (!id_kiinteisto) return (
    <div className="my-4 p-3 border rounded text-muted text-center small bg-light">
      <i className="bi bi-table me-1" />
      PTS (Kuntoarviovalikko) — näkyy kun raportti on sidottu kiinteistöön
    </div>
  );

  if (loading) return <div className="my-4 text-center text-muted small">Ladataan…</div>;

  const extraCols = 1;
  const totalCols = 2 + numYears + extraCols;

  return (
    <div className="my-4 dynamic-pts-taulu" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {error && <div className="alert alert-danger py-1 small">{error}</div>}

      {/* Project selector */}
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap" style={{ minHeight: 32 }}>
        {linkedProjectId ? (
          <span className="text-muted small">
            <i className="bi bi-link-45deg me-1" />
            Projekti: Kuntoarviovalikosta —{' '}
            <strong className="text-dark">
              {projects.find(p => p.id === activeProjectId)?.name || `Projekti ${activeProjectId}`}
            </strong>
          </span>
        ) : (
          <>
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: 260 }}
              value={activeProjectId ?? ''}
              onChange={e => handleSelectProject(e.target.value)}
            >
              {projects.length === 0 && <option value="">— Ei projekteja —</option>}
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name || `Projekti ${p.id}`}</option>
              ))}
            </select>

            {activeProjectId && (
              <button
                className="btn btn-sm btn-outline-danger"
                title="Poista projekti"
                onClick={() => { setDeleteProjectModal(true); setDeleteProjectInput(''); }}
              >
                <i className="bi bi-trash" />
              </button>
            )}
          </>
        )}

        {/* Project creation only makes sense when this table is picking its own
            project — once Kuntoarviovalikko is driving (linkedProjectId set),
            a new project can only be created from there, not competing here. */}
        {!linkedProjectId && (
          showNewProjectInput ? (
              <>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 200 }}
                  placeholder="Projektin nimi…"
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleCreateProject()}
                  autoFocus
                />
                <button
                  className="btn btn-sm btn-success"
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim() || creatingProject}
                >
                  {creatingProject ? '…' : 'Luo'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => { setShowNewProjectInput(false); setNewProjectName(''); }}>
                  Peru
                </button>
              </>
          ) : (
            <button className="btn btn-sm btn-outline-success" onClick={() => setShowNewProjectInput(true)}>
              + Uusi projekti
            </button>
          )
        )}

        <span className={`pts-saved-indicator${savedFlash ? ' visible' : ''}`}>
          <i className="bi bi-check-circle-fill" />
          Tallennettu
        </span>
      </div>

      {!activeProjectId ? (
        <div className="text-center text-muted small py-3">
          {linkedProjectId ? 'Ladataan kuntoarviovalikon projektia…' : 'Luo uusi projekti aloittaaksesi.'}
        </div>
      ) : sections.length === 0 ? (
        <div className="text-center text-muted small py-3">
          Ei osa-alueita. Lisää osa-alue alta tai luo alikategorioita kuntoarviovalikossa.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table className="table table-sm table-borderless mb-0" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th colSpan={totalCols} className="bg-success text-white p-2">
                  <div className="small text-end">
                    Kustannusarvio (x 1000€) Kustannustaso {currentYear} sis. Alv 25,5%
                  </div>
                </th>
              </tr>
              <tr>
                <th className="bg-success text-white text-start">
                  <input
                    type="text"
                    value={projectName}
                    onChange={e => setProjectName(e.target.value)}
                    className="form-control form-control-sm bg-success text-white border-0 fw-bold"
                    style={{ minWidth: 160 }}
                    placeholder="Otsikko…"
                  />
                </th>
                <th className="bg-success text-white text-center"></th>
                {years.map(y => (
                  <th key={y} className="bg-success text-white text-center px-2 fw-bold">{y}</th>
                ))}
                <th className="bg-success text-white"></th>
              </tr>
            </thead>

            <tbody>
              {sections.map(subcat => {
                const sec = data[subcat] ?? makeEmptySection(numYears);
                const kl = sec.kl?.trim() || '';

                return (
                  <React.Fragment key={subcat}>
                    <tr>
                      <td className="bg-light fw-semibold text-dark p-2" style={{ minWidth: 180 }}>
                        {subcat}
                      </td>
                      <td className="bg-light text-center fw-semibold align-middle" style={{ minWidth: 70 }}>
                        <select
                          value={sec.kl ?? 'KL3'}
                          onChange={e => updateSectionKL(subcat, e.target.value)}
                          className="form-select form-select-sm"
                          style={{ minWidth: 60, maxWidth: 75, fontSize: '0.75rem', padding: '2px 4px', color: kl ? getKLColor(kl) : 'inherit' }}
                        >
                          {[' ', 'KL1', 'KL2', 'KL3', 'KL4', 'KL5'].map(k => (
                            <option key={k} value={k} style={{ color: k.trim() ? getKLColor(k) : 'inherit' }}>{k}</option>
                          ))}
                        </select>
                      </td>
                      <td className="bg-light" colSpan={numYears + extraCols}>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmDelete(subcat)}>×</button>
                        </div>
                      </td>
                    </tr>

                    <tr>
                      <td colSpan={2} className="bg-light"></td>
                      {years.map(y => (
                        <td key={y} className="bg-light text-center text-muted px-2">{y}</td>
                      ))}
                      <td className="bg-light"></td>
                    </tr>


                    {sec.items.map((item, itemIdx) => (
                      <tr key={itemIdx}>
                        <td colSpan={2} style={{ minWidth: 180, maxWidth: 320, paddingLeft: '1.5rem' }}>
                          <PlainTextExpandingInput
                            namespace={`pts-label-${subcat}-${itemIdx}`}
                            value={item.label || ''}
                            onChange={val => updateItem(subcat, itemIdx, val)}
                            placeholder="Toimenpide…"
                          />
                        </td>

                        {item.values.map((val, yearIdx) => (
                          <td key={yearIdx} className="text-center px-1">
                            <input
                              type="text"
                              value={val === 0 || val === '0' ? '' : val}
                              onChange={e => updateValues(subcat, itemIdx, yearIdx, e.target.value)}
                              className={`form-control form-control-sm text-center ${
                                isNumeric(val) && Number(val) > 0 ? 'bg-success text-white' : ''
                              }`}
                              style={{ width: '100%', minWidth: 40, padding: '2px 4px' }}
                            />
                          </td>
                        ))}

                        <td>
                          <div className="d-flex gap-1">
                            {item.label?.trim() && (() => {
                              const alreadyIn = isInTekniikkaPTS(item.label);
                              return (
                                <button
                                  className={`btn btn-sm ${alreadyIn ? 'btn-success' : 'btn-outline-primary'}`}
                                  style={{ fontSize: '0.7rem', padding: '1px 5px' }}
                                  title={alreadyIn ? 'Löytyy jo tekniikka PTS:stä' : 'Vie tekniikka PTS:ään'}
                                  onClick={() => {
                                    const matchCat = 'Rakennetekniikka';
                                    const matchSecs = getCopySections(matchCat);
                                    const matchIdx = matchSecs.findIndex(s =>
                                      s.name?.toLowerCase() === subcat.toLowerCase()
                                    );
                                    setCopyModal({ label: item.label, values: item.values });
                                    setCopyCategory(matchCat);
                                    setCopySectionIdx(matchIdx >= 0 ? matchIdx : 0);
                                  }}
                                >
                                  {alreadyIn ? <><i className="bi bi-check2" /> PTS</> : <><i className="bi bi-arrow-right" /> PTS</>}
                                </button>
                              );
                            })()}
                            <button className="btn btn-sm btn-outline-danger" onClick={() => removeRow(subcat, itemIdx)}>×</button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    <tr>
                      <td colSpan={totalCols} className="bg-light" style={{ paddingTop: 4, paddingBottom: 4, paddingLeft: '1.5rem' }}>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => addRow(subcat)}>+</button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>

            <tfoot>
              <tr className="fw-bold">
                <td className="bg-success text-white text-start">YHTEENSÄ</td>
                <td className="bg-success text-white"></td>
                {totals.map((sum, idx) => (
                  <td key={idx} className="bg-success text-white text-center font-monospace px-2">
                    {sum || ''}
                  </td>
                ))}
                <td className="bg-success text-white"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {activeProjectId && (
        <div className="d-flex align-items-center gap-2 mt-2">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: 240 }}
            placeholder="Uusi osa-alue…"
            value={newCatInput}
            onChange={e => setNewCatInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAddCategory()}
          />
          <button
            className="btn btn-sm btn-outline-success"
            onClick={handleAddCategory}
            disabled={!newCatInput.trim() || sections.includes(newCatInput.trim())}
          >
            + Lisää osa-alue
          </button>
        </div>
      )}

      {copyModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Vie kortin PTS:ään</h5>
              </div>
              <div className="modal-body">
                <p className="mb-3 text-muted small">
                  Kopioidaan: <strong>{copyModal.label || '(tyhjä)'}</strong>
                </p>
                <div className="mb-3">
                  <label className="form-label small fw-semibold">Taulukko</label>
                  <select
                    className="form-select form-select-sm"
                    value={copyCategory}
                    onChange={e => { setCopyCategory(e.target.value); setCopySectionIdx(0); }}
                  >
                    {COPY_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-semibold">Osa-alue</label>
                  <select
                    className="form-select form-select-sm"
                    value={copySectionIdx}
                    onChange={e => setCopySectionIdx(Number(e.target.value))}
                  >
                    {getCopySections(copyCategory).map((sec, idx) => (
                      <option key={idx} value={idx}>{sec.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setCopyModal(null)}>Peruuta</button>
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    insertTableRow(copyCategory, copySectionIdx, { label: copyModal.label, values: copyModal.values });
                    setCopyModal(null);
                  }}
                >
                  Vie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteProjectModal && (() => {
        const activeProject = projects.find(p => p.id === activeProjectId);
        const expectedName = activeProject?.name || '';
        const matches = deleteProjectInput === expectedName;
        return (
          <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-danger">
                  <h5 className="modal-title text-danger">Poista projekti</h5>
                </div>
                <div className="modal-body">
                  <p className="mb-2">
                    Tämä poistaa projektin <strong>{expectedName}</strong> ja kaikki sen tiedot pysyvästi.
                    Toimintoa ei voi perua.
                  </p>
                  <p className="text-muted small mb-1">
                    Vahvista kirjoittamalla projektin nimi:
                  </p>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder={expectedName}
                    value={deleteProjectInput}
                    onChange={e => setDeleteProjectInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && matches && !deletingProject && handleDeleteProject()}
                    autoFocus
                  />
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary btn-sm" onClick={() => { setDeleteProjectModal(false); setDeleteProjectInput(''); }} disabled={deletingProject}>
                    Peruuta
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={handleDeleteProject} disabled={!matches || deletingProject}>
                    {deletingProject ? 'Poistetaan…' : 'Poista projekti'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {confirmDelete && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Poista osa-alue</h5>
              </div>
              <div className="modal-body">
                Poistetaanko osa-alue <strong>{confirmDelete}</strong> ja kaikki sen rivit?
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(null)}>Peruuta</button>
                <button className="btn btn-danger btn-sm" onClick={() => removeSection(confirmDelete)}>Poista</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
